import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerFooter, DrawerDescription } from '@/components/ui/drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useDevice } from '@/contexts/DeviceContext';
import { useLayout } from '@/contexts/LayoutContext';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../api';
import { Search, Plus, Loader2, CheckCircle2, Clock, CheckCircle, FileText, Globe } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useItunesSearch } from '@/hooks/useItunesSearch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useKeyboardHeight } from '@/hooks/useKeyboardHeight';
import { cn } from '@/lib/utils';
import { ProfileAvatar } from '@/components/ProfileAvatar';

const Requests: React.FC = () => {
  const { toast } = useToast();
  const { selectedDevice } = useDevice();
  const { setBottomNavVisible } = useLayout();
  const { serial, source, isLoading: isSerialLoading } = useEffectiveSerial();
  const { keyboardHeight, isKeyboardOpen } = useKeyboardHeight();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const webInputRef = useRef<HTMLInputElement>(null);

  // Debug logging - only in development
  if (import.meta.env.DEV) {
    console.log('Requests - Serial Resolution:', { serial, source });
  }
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isWebDrawerOpen, setIsWebDrawerOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [webRequestFilter, setWebRequestFilter] = useState<'pending' | 'past'>('pending');
  const [requestFilter, setRequestFilter] = useState<'pending' | 'past'>('pending');

  // iTunes Search
  const { results: appSearchResults, isLoading: isSearching, error: searchError } = useItunesSearch(searchQuery);

  // Fetch app requests for this device - wait for serial resolution to complete
  const appRequestsData = useQuery(
    api.requests.list.myAppRequests, 
    !isSerialLoading && serial ? { serial, paginationOpts: { numItems: 20, cursor: null } } : "skip"
  );

  // Fetch web requests for this device
  const webRequestsData = useQuery(
    api.requests.list.myWebRequests,
    !isSerialLoading && serial ? { serial, paginationOpts: { numItems: 20, cursor: null } } : "skip"
  );
  
  // Handle error state (e.g., device not found in backend)
  const hasDeviceError = appRequestsData instanceof Error;
  const appRequests = (!hasDeviceError && appRequestsData?.page) ? appRequestsData.page : [];
  
  const hasWebDeviceError = webRequestsData instanceof Error;
  const webRequests = (!hasWebDeviceError && webRequestsData?.page) ? webRequestsData.page : [];

  // Mutations to create requests
  const makeRequest = useMutation(api.requests.create.appRequest);
  const makeWebRequest = useMutation(api.requests.create.webRequest);

  // Filter requests by pending (no responseTime) or past (has responseTime)
  const pendingRequests = appRequests.filter((req: any) => !req.responseTime);
  const pastRequests = appRequests.filter((req: any) => !!req.responseTime);
  const filteredRequests = requestFilter === 'pending' ? pendingRequests : pastRequests;

  // Determine status based on responseTime and response field
  const getRequestStatus = (request: any): 'pending' | 'approved' | 'rejected' => {
    if (!request.responseTime) {
      return 'pending';
    }
    // Has responseTime = past request, check response field
    if (request.response === 'level' || request.response === 'device' || request.response === 'allow') {
      return 'approved';
    } else if (request.response === 'deny') {
      return 'rejected';
    }
    return 'approved'; // default for past
  };

  const getStatusBadgeVariant = (status: 'pending' | 'approved' | 'rejected') => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'rejected':
        return 'destructive';
      case 'approved':
        return 'default';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status: 'pending' | 'approved' | 'rejected') => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Pending';
      default:
        return 'Pending';
    }
  };

  // Safely format request date using available timestamps
  const formatRequestDate = (req: any) => {
    const ts = req?._creationTime ?? req?.responseTime ?? req?.rejectedAt;
    if (!ts) return '';
    const d = new Date(Number(ts));
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSubmitRequest = async () => {
    if (!selectedApp) {
      toast({
        title: "App Required",
        description: "Please select an app to request.",
        variant: "destructive",
      });
      return;
    }

    if (!serial) {
      toast({
        title: "Device Required",
        description: "No device serial available.",
        variant: "destructive",
      });
      return;
    }

    try {
      await makeRequest({
        app: selectedApp.bundleId || selectedApp.trackId.toString(),
        bundleId: selectedApp.bundleId || selectedApp.trackId.toString(),
        serial: serial,
        message: `Request for ${selectedApp.trackName} (Bundle ID: ${selectedApp.bundleId || 'N/A'})`,
        trackId: selectedApp.trackId.toString(),
        name: selectedApp.trackName,
        iconUrl: selectedApp.artworkUrl100,
        category: selectedApp.primaryGenreName,
        version: selectedApp.version
      } as any);

      toast({
        title: "Request Submitted",
        description: "Your app request has been submitted for review.",
        duration: 2000,
      });

      // Reset form and close drawer
      setSelectedApp(null);
      setSearchQuery('');
      setIsDrawerOpen(false);
      setBottomNavVisible(true);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Failed to submit request.",
        variant: "destructive",
      });
    }
  };

  // Strip protocol from URL input
  const stripProtocol = (url: string) => {
    return url.replace(/^https?:\/\//i, '').trim();
  };

  const handleSubmitWebRequest = async () => {
    const cleanUrl = stripProtocol(websiteUrl);
    
    // Validate URL
    if (!cleanUrl) {
      toast({
        title: "Website Required",
        description: "Please enter a website domain.",
        variant: "destructive",
      });
      return;
    }

    // Basic domain validation (allow domains like example.com)
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    if (!domainRegex.test(cleanUrl)) {
      toast({
        title: "Invalid Domain",
        description: "Please enter a valid domain (e.g., example.com).",
        variant: "destructive",
      });
      return;
    }

    if (!serial) {
      toast({
        title: "Device Required",
        description: "No device serial available.",
        variant: "destructive",
      });
      return;
    }

    try {
      await makeWebRequest({
        url: cleanUrl,
        serial: serial
      });

      toast({
        title: "Request Submitted",
        description: "Your website request has been submitted for review.",
        duration: 2000,
      });

      // Reset form and close drawer
      setWebsiteUrl('');
      setIsWebDrawerOpen(false);
      setBottomNavVisible(true);
    } catch (error) {
      console.error('Error submitting web request:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Failed to submit request.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                <FileText className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">Requests</h1>
            </div>
            <ProfileAvatar />
          </div>
          <p className="text-[13px] text-muted-foreground mt-3">Request new apps or websites</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="px-4 pt-6">
        <Tabs defaultValue="app" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="app">App Request</TabsTrigger>
            <TabsTrigger value="web">Web Request</TabsTrigger>
          </TabsList>

          {/* App Request Tab */}
          <TabsContent value="app" className="space-y-4">
            {/* New Request Button at Top */}
            <Drawer open={isDrawerOpen} onOpenChange={(open) => {
              setIsDrawerOpen(open);
              setBottomNavVisible(!open);
              if (!open) {
                setSelectedApp(null);
                setSearchQuery('');
              }
            }}>
              <DrawerTrigger asChild>
                <Button className="w-full gap-2">
                  <Plus className="w-4 h-4" />
                  New App Request
                </Button>
              </DrawerTrigger>
              <DrawerContent 
                className="keyboard-drawer flex flex-col max-h-[90dvh]"
                style={{ 
                  maxHeight: isKeyboardOpen 
                    ? `calc(100dvh - ${keyboardHeight}px - 20px)` 
                    : undefined
                }}
              >
                <DrawerHeader className={cn(
                  "flex-shrink-0 transition-all duration-200",
                  isKeyboardOpen ? "py-2" : "pt-6"
                )}>
                  <DrawerTitle className={isKeyboardOpen ? "text-base" : ""}>
                    Request New App
                  </DrawerTitle>
                  {!isKeyboardOpen && (
                    <DrawerDescription>Search and select an app to request access</DrawerDescription>
                  )}
                </DrawerHeader>
                
                <div className="flex-1 flex flex-col min-h-0 px-4">
                  {/* Search Input - Always visible */}
                  <div className="relative flex-shrink-0 py-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search for apps..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 border-border bg-background focus-visible:ring-1 focus-visible:ring-primary"
                      style={{ fontSize: '16px' }}
                    />
                  </div>

                  {/* Search Results - Scrollable area that fills remaining space */}
                  <div 
                    className="flex-1 overflow-y-auto overscroll-contain"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      minHeight: 0
                    }}
                  >
                    <div className="space-y-2 pb-4 pr-2" role="listbox" aria-label="App search results">
                      {isSearching ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : searchError ? (
                        <div className="text-center py-8 text-destructive text-sm">
                          {searchError}
                        </div>
                      ) : searchQuery.trim() && appSearchResults.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          No apps found
                        </div>
                      ) : appSearchResults.length > 0 ? (
                        appSearchResults.map((app) => (
                          <div
                            key={app.trackId}
                            onClick={() => setSelectedApp(app)}
                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedApp?.trackId === app.trackId
                                ? 'bg-primary/10 border-primary'
                                : 'hover:bg-muted/50'
                            }`}
                          >
                            <img
                              src={app.artworkUrl100}
                              alt={app.trackName}
                              className="w-12 h-12 rounded-lg"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{app.trackName}</p>
                              <p className="text-xs text-muted-foreground truncate">{app.artistName}</p>
                              {app.primaryGenreName && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  {app.primaryGenreName}
                                </Badge>
                              )}
                            </div>
                            {selectedApp?.trackId === app.trackId && (
                              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          Start typing to search for apps
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Selection Confirmation Banner */}
                {selectedApp && (
                  <div className="flex-shrink-0 mx-4 mb-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-2 animate-fade-in">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-primary truncate">
                      {selectedApp.trackName} selected — Ready to submit
                    </span>
                  </div>
                )}

                <DrawerFooter className={cn(
                  "flex-row gap-2 flex-shrink-0 bg-background border-t",
                  isKeyboardOpen ? "py-2" : "pt-4 pb-8 safe-area-bottom"
                )}>
                  <Button onClick={handleSubmitRequest} disabled={!selectedApp} size="sm" className="flex-1">
                    Submit Request
                  </Button>
                  <Button variant="outline" onClick={() => setIsDrawerOpen(false)} size="sm" className="flex-1">
                    Cancel
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

      {/* App Requests List */}
            <div className="space-y-4">
              {/* Pending/Past Filter Tabs */}
              <div className="flex gap-2">
                <Button
                  variant={requestFilter === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRequestFilter('pending')}
                  className="gap-1.5"
                >
                  <Clock className="w-3.5 h-3.5" />
                  Pending
                  {pendingRequests.length > 0 && (
                    <span className="ml-1 text-xs bg-background/20 px-1.5 py-0.5 rounded-full">
                      {pendingRequests.length}
                    </span>
                  )}
                </Button>
                <Button
                  variant={requestFilter === 'past' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRequestFilter('past')}
                  className="gap-1.5"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Past
                </Button>
              </div>

              {/* Requests List */}
              {isSerialLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                      <Skeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : source === 'default' ? (
                <div className="text-center py-8 space-y-2 border rounded-lg bg-background">
                  <p className="text-muted-foreground text-sm">We couldn't identify your device</p>
                  <p className="text-xs text-muted-foreground">Please reopen the app from your device link or contact your admin.</p>
                </div>
              ) : hasDeviceError ? (
                <div className="text-center py-8 space-y-2 border rounded-lg bg-background">
                  <p className="text-muted-foreground text-sm">Device not found in system</p>
                  <p className="text-xs text-muted-foreground">Please contact your administrator</p>
                </div>
              ) : appRequestsData === undefined ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                      <Skeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  ))}
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-background">
                  {requestFilter === 'pending' ? 'No pending requests' : 'No past requests'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredRequests.map((request: any) => {
                    const status = getRequestStatus(request);
                    return (
                      <div key={request._id} className="flex items-center gap-3 p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors">
                        {request.iconUrl && (
                          <img 
                            src={request.iconUrl} 
                            alt={request.name || 'App'} 
                            className="w-12 h-12 rounded-lg"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{request.name || request.app}</p>
                          <p className="text-xs text-muted-foreground">
                            {request.category && <span>{request.category} • </span>}
                            {formatRequestDate(request) || 'Requested'}
                          </p>
                        </div>
                        <Badge variant={getStatusBadgeVariant(status)}>
                          {getStatusLabel(status)}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Web Request Tab */}
          <TabsContent value="web" className="space-y-4">
            {/* New Request Button at Top */}
            <Drawer 
              open={isWebDrawerOpen} 
              dismissible={false}
              // We handle keyboard positioning ourselves; this avoids iOS quirks where the drawer can jump/close
              // when the keyboard's "Done/Check" key is pressed.
              repositionInputs={false}
              onOpenChange={(open) => {
                setIsWebDrawerOpen(open);
                setBottomNavVisible(!open);
                if (!open) {
                  setWebsiteUrl('');
                }
              }}
            >
              <DrawerTrigger asChild>
                <Button className="w-full gap-2">
                  <Plus className="w-4 h-4" />
                  New Web Request
                </Button>
              </DrawerTrigger>
              <DrawerContent 
                className="keyboard-drawer flex flex-col max-h-[90dvh]"
                style={{
                  // Lift drawer above the on-screen keyboard so the footer/buttons stay reachable.
                  bottom: isKeyboardOpen ? `${keyboardHeight}px` : undefined,
                  // Constrain height within the remaining visible viewport.
                  maxHeight: isKeyboardOpen ? `calc(100vh - ${keyboardHeight}px - 20px)` : undefined,
                }}
                onPointerDownOutside={(e) => {
                  // Always prevent closing on outside tap - use Cancel button instead
                  e.preventDefault();
                }}
                onInteractOutside={(e) => {
                  // Always prevent closing on outside interaction - use Cancel button instead
                  e.preventDefault();
                }}
              >
                <DrawerHeader className={cn(
                  "flex-shrink-0 transition-all duration-200",
                  isKeyboardOpen ? "py-2" : "pt-6"
                )}>
                  <DrawerTitle className={isKeyboardOpen ? "text-base" : ""}>
                    Request Website Access
                  </DrawerTitle>
                  {!isKeyboardOpen && (
                    <DrawerDescription>Enter the website URL you need access to</DrawerDescription>
                  )}
                </DrawerHeader>
                
                <div className="px-4 pt-2 space-y-4 pb-4">
                  {/* Website URL Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Website</label>
                    <Input
                      ref={webInputRef}
                      type="text"
                      placeholder="example.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(stripProtocol(e.target.value))}
                      onKeyDown={(e) => {
                        // iOS keyboard "Done/Check" sends Enter; don't let it trigger any implicit actions.
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          (e.currentTarget as HTMLInputElement).blur();
                        }
                      }}
                      className="border-border bg-background focus-visible:ring-1 focus-visible:ring-primary"
                      style={{ fontSize: '16px' }}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                  </div>
                </div>
                
                <DrawerFooter className={cn(
                  "flex-row gap-2 flex-shrink-0 bg-background border-t",
                  isKeyboardOpen ? "py-2" : "pt-4 pb-8 safe-area-bottom"
                )}>
                  <Button 
                    onClick={handleSubmitWebRequest} 
                    disabled={!websiteUrl.trim()} 
                    size="sm" 
                    className="flex-1"
                    type="button"
                  >
                    Submit Request
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsWebDrawerOpen(false)} 
                    size="sm" 
                    className="flex-1"
                    type="button"
                  >
                    Cancel
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

            {/* Web Requests List */}
            <div className="space-y-4">
              {/* Pending/Past Filter Tabs */}
              <div className="flex gap-2">
                <Button
                  variant={webRequestFilter === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setWebRequestFilter('pending')}
                  className="gap-1.5"
                >
                  <Clock className="w-3.5 h-3.5" />
                  Pending
                  {webRequests.filter((req: any) => !req.responseTime).length > 0 && (
                    <span className="ml-1 text-xs bg-background/20 px-1.5 py-0.5 rounded-full">
                      {webRequests.filter((req: any) => !req.responseTime).length}
                    </span>
                  )}
                </Button>
                <Button
                  variant={webRequestFilter === 'past' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setWebRequestFilter('past')}
                  className="gap-1.5"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Past
                </Button>
              </div>

              {/* Web Requests List */}
              {isSerialLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                      <Skeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : source === 'default' ? (
                <div className="text-center py-8 space-y-2 border rounded-lg bg-background">
                  <p className="text-muted-foreground text-sm">We couldn't identify your device</p>
                  <p className="text-xs text-muted-foreground">Please reopen the app from your device link or contact your admin.</p>
                </div>
              ) : hasWebDeviceError ? (
                <div className="text-center py-8 space-y-2 border rounded-lg bg-background">
                  <p className="text-muted-foreground text-sm">Device not found in system</p>
                  <p className="text-xs text-muted-foreground">Please contact your administrator</p>
                </div>
              ) : webRequestsData === undefined ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                      <Skeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  ))}
                </div>
              ) : (() => {
                const filteredWebRequests = webRequestFilter === 'pending' 
                  ? webRequests.filter((req: any) => !req.responseTime)
                  : webRequests.filter((req: any) => !!req.responseTime);
                
                if (filteredWebRequests.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-background">
                      {webRequestFilter === 'pending' ? 'No pending requests' : 'No past requests'}
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    {filteredWebRequests.map((request: any, index: number) => {
                      const status = getRequestStatus(request);
                      return (
                        <div key={request._id || index} className="flex items-center gap-3 p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors">
                          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                            <Globe className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{request.url}</p>
                            <p className="text-xs text-muted-foreground">
                              {request.category && <span>{request.category} • </span>}
                              {formatRequestDate(request) || 'Requested'}
                            </p>
                          </div>
                          <Badge variant={getStatusBadgeVariant(status)}>
                            {getStatusLabel(status)}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Requests;
